import React, { useState, useEffect } from 'react';  
import {  
    Input,  
    Button,  
    Form,  
    Skeleton,  
    Alert,  
    Typography,  
    message,  
    Select,  
    Checkbox,  
    Card,  
    Space,  
    InputNumber,  
    List,  
    Modal,  
    DatePicker,  
    Switch,  
} from 'antd';  
import {
    UploadOutlined
} from '@ant-design/icons'
import {  
    fetchAllCompetitions,  
    fetchProvinces,  
    createProvince,  
    deleteProvince,  
    updateProvince, 
    uploadCertificate
} from '../../api/competition';
import { updateActiveProvince, createUser } from '../../api/user';
import { updateSettings } from '../../api/settings';  
import { fetchSelectedCompetition } from '../../api/selectedCompetition';  
import { useSettings } from '../../contexts/settingsContext';  
import { initialState } from '../../reducers/settingsReducer';  
import ReactQuill from 'react-quill';  
import 'react-quill/dist/quill.snow.css';  
import moment from 'moment';  
import { loadState, saveState } from '../../stores/store';
  
const { Title } = Typography;  
const { Option } = Select;  
  
const Settings = () => {  
    const { state, dispatch } = useSettings();  
    const [isLoading, setIsLoading] = useState(true);  
    const [error, setError] = useState(null);  
    const [competitions, setCompetitions] = useState([]);  
    const [provinces, setProvinces] = useState([]);  
    const [savingSettings, setSavingSettings] = useState(false);  
    const [isAddProvinceModalVisible, setIsAddProvinceModalVisible] = useState(false);  
    const [isEditProvinceModalVisible, setIsEditProvinceModalVisible] = useState(false);  
    const [newProvince, setNewProvince] = useState({  
        province: '',  
        location: '',  
        date: null,  
        contact_person: '',  
        contact_number: '',  
        active: false,  
    });  
    const [editingProvince, setEditingProvince] = useState(null);  
    const [user, setUser] = useState(loadState().user);
    const [file, setFile] = useState(null);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
  
    // Initialize local settings with three rounds if not present  
    const initializeRounds = (rounds) => {  
        const defaultRounds = [  
            { roundNumber: 1, baseFee: 0, additionalFee: 0 },  
            { roundNumber: 2, baseFee: 0, additionalFee: 0 },  
            { roundNumber: 3, baseFee: 0, additionalFee: 0 },  
        ];  
        return defaultRounds.map((defaultRound, index) => {  
            return rounds[index] || defaultRound;  
        });  
    };  
  
    const initializeSettings = (settings) => ({  
        ...settings,  
        rounds: initializeRounds(settings.rounds || []),  
    });  
  
    const [localSettings, setLocalSettings] = useState(  
        initializeSettings(state) || initializeSettings(initialState)  
    );  
  
    const fetchSettings = async (competitionId) => {  
        try {  
            const response = await fetch(`/api/settings?competitionId=${competitionId}`, {  
                credentials: 'include',  
            });  
            if (!response.ok) {  
                throw new Error(`Error: ${response.status} ${response.statusText}`);  
            }  
            const fetchedSettings = await response.json();  
            if (!fetchedSettings) {  
                setLocalSettings(initializeSettings(initialState));  
            } else {  
                const rounds = initializeRounds(fetchedSettings.rounds || []);  
                const initializedSettings = {  
                    ...initializeSettings(fetchedSettings),  
                    rounds,  
                };  
                setLocalSettings(initializedSettings);  
                dispatch({ type: 'UPDATE_SETTINGS', payload: initializedSettings });  
            }  
        } catch (error) {  
            setError(error.message);  
        } finally {  
            setIsLoading(false);  
        }  
    };  
  
    useEffect(() => {  
        if (state.selectedCompetition) {  
            fetchSettings(state.selectedCompetition._id);  
        }  
    }, [state.selectedCompetition, dispatch]);  
  
    useEffect(() => {  
        const fetchAllTheCompetitions = async () => {  
            try {  
                const competitions = await fetchAllCompetitions();  
                setCompetitions(competitions);  
            } catch (err) {  
                console.error('Error fetching competitions', err);  
            }  
        };  
        fetchAllTheCompetitions();  
    }, []);  
  
    useEffect(() => {  
        const fetchProvincesForCompetition = async () => {  
            if (state.selectedCompetition) {  
                try {  
                    const provinces = await fetchProvinces(state.selectedCompetition._id);  
                    setProvinces(provinces);  
                } catch (error) {  
                    console.error('Error fetching provinces:', error);  
                    setProvinces([]); // Clear the provinces list if error occurs  
                }  
            }  
        };  
  
        fetchProvincesForCompetition();  
    }, [state.selectedCompetition]);  
  
    const handleInputChange = (e) => {  
        const { name, value } = e.target;  
        if (name.includes('.')) {  
            const [section, field, subField] = name.split('.');  
            if (subField) {  
                // For nested fields like contactDetails.emergencyContact.name  
                setLocalSettings((prevSettings) => ({  
                    ...prevSettings,  
                    [section]: {  
                        ...prevSettings[section],  
                        [field]: {  
                            ...prevSettings[section][field],  
                            [subField]: value,  
                        },  
                    },  
                }));  
            } else {  
                setLocalSettings((prevSettings) => ({  
                    ...prevSettings,  
                    [section]: {  
                        ...prevSettings[section],  
                        [field]: value,  
                    },  
                }));  
            }  
        } else {  
            setLocalSettings((prevSettings) => ({  
                ...prevSettings,  
                [name]: value,  
            }));  
        }  
    };  
  
    const handleQuillChange = (value) => {  
        setLocalSettings((prevSettings) => ({  
            ...prevSettings,  
            termsConditions: value,  
        }));  
    };  
  
    const handleCheckboxChange = (e) => {  
        const { name, checked } = e.target;  
        setLocalSettings((prevSettings) => ({  
            ...prevSettings,  
            [name]: checked,  
        }));  
    };  
  
    const handleRoundChange = (index, field, value) => {  
        setLocalSettings((prevSettings) => {  
            const updatedRounds = [...prevSettings.rounds];  
            updatedRounds[index] = {  
                ...updatedRounds[index],  
                [field]: value,  
                roundNumber: index + 1,  
            };  
            return {  
                ...prevSettings,  
                rounds: updatedRounds,  
            };  
        });  
    };  
  
    const handleGradingDetailsChange = (e) => {  
        const { name, value } = e.target;  
        setLocalSettings((prevSettings) => ({  
            ...prevSettings,  
            gradingDetails: {  
                ...prevSettings.gradingDetails,  
                [name]: Number(value),  
            },  
        }));  
    };  
  
    const handleCompetitionSelect = async (value) => {  
        const selectedCompetition = competitions.find((comp) => comp._id === value);  
        setLocalSettings((prevSettings) => ({  
            ...prevSettings,  
            competition: selectedCompetition,  
        }));  
        dispatch({  
            type: 'UPDATE_COMPETITION',  
            payload: { _id: selectedCompetition._id, name: selectedCompetition.name },  
        });  
  
        fetchSettings(selectedCompetition._id);  
  
        try {  
            // Fetch provinces for the selected competition  
            const provinces = await fetchProvinces(selectedCompetition._id);  
            setProvinces(provinces);  
        } catch (error) {  
            console.error('Error fetching provinces:', error);  
            setProvinces([]); // Clear the provinces list if error occurs  
        }  
  
        try {  
            await fetchSelectedCompetition();  
        } catch (error) {  
            console.error('Error updating selected competition:', error);  
        }  
    };  
  
    const handleSaveSettings = async () => {  
        try {  
            setSavingSettings(true);  
            const { competition, certificate, ...settingsToSave } = localSettings;
            const comp = JSON.parse(localStorage.getItem('selectedCompetition'));  
              
            const validRounds = settingsToSave.rounds.map((round, index) => ({  
                roundNumber: index + 1,  
                baseFee: round.baseFee,  
                additionalFee: round.additionalFee,  
            }));  
              
            settingsToSave.rounds = validRounds;  
              
            await updateSettings(settingsToSave, comp._id);  
            await fetchSettings(comp._id);  
              
            message.success('Settings saved successfully!');  
            setSavingSettings(false);  
        } catch (error) {  
            setSavingSettings(false);  
            setError(error.message);  
            message.error('Error saving settings!');  
        }  
    };  
  
    const handleSelectedRoundChange = (value) => {  
        setLocalSettings((prevSettings) => ({  
            ...prevSettings,  
            selectedRound: value,  
        }));  
    };  

    const handleMinimumAgeChange = (value) => {  
        setLocalSettings((prevSettings) => ({  
            ...prevSettings,  
            minimumAge: value,  
        }));  
    };  
  
    const handleAddProvince = () => {  
        setIsAddProvinceModalVisible(true);  
    };  
  
    const handleAddProvinceOk = async () => {  
        try {  
            const competitionId = state.selectedCompetition._id;  
            const provinceData = {  
                ...newProvince,  
                date: newProvince.date ? newProvince.date.toISOString() : null,  
            };  
            await createProvince(competitionId, provinceData);  
            // Fetch the updated list of provinces  
            const provinces = await fetchProvinces(competitionId);  
            setProvinces(provinces);  
            message.success('Province added successfully');  
        } catch (error) {  
            message.error('Error adding province');  
        } finally {  
            setIsAddProvinceModalVisible(false);  
            setNewProvince({  
                province: '',  
                location: '',  
                date: null,  
                contact_person: '',  
                contact_number: '',  
                active: false,  
            });  
        }  
    };  
  
    const handleAddProvinceCancel = () => {  
        setIsAddProvinceModalVisible(false);  
        setNewProvince({  
            province: '',  
            location: '',  
            date: null,  
            contact_person: '',  
            contact_number: '',  
            active: false,  
        });  
    };  
  
    const handleEditProvince = (province) => {  
        setEditingProvince({  
            ...province,  
            date: province.date ? moment(province.date) : null,  
        });  
        setIsEditProvinceModalVisible(true);  
    };  
  
    const handleEditProvinceOk = async () => {  
        try {  
            const competitionId = state.selectedCompetition._id;  
      
            const provinceData = {  
                province: editingProvince.province || '',  
                location: editingProvince.location || '',  
                date: editingProvince.date ? editingProvince.date.toISOString() : null,  
                contact_person: editingProvince.contact_person || '',  
                contact_number: editingProvince.contact_number || '',  
                active: editingProvince.active || false,  
            };  
      
            await updateProvince(competitionId, editingProvince._id, provinceData);  
      
            // Fetch the updated list of provinces  
            const provinces = await fetchProvinces(competitionId);  
            setProvinces(provinces);  
            message.success('Province updated successfully');  
        } catch (error) {  
            console.error('Error updating province:', error);  
            message.error('Error updating province');  
        } finally {  
            setIsEditProvinceModalVisible(false);  
            setEditingProvince(null);  
        }  
    };  
  
    const handleEditProvinceCancel = () => {  
        setIsEditProvinceModalVisible(false);  
        setEditingProvince(null);  
    };  
  
    const handleDeleteProvince = async (provinceId) => {  
        try {  
            const competitionId = state.selectedCompetition._id;  
            await deleteProvince(competitionId, provinceId);  
            // Fetch the updated list of provinces  
            const provinces = await fetchProvinces(competitionId);  
            setProvinces(provinces);  
            message.success('Province deleted successfully');  
        } catch (error) {  
            message.error('Error deleting province');  
        }  
    };  
  
    const handleActiveStatusChange = async (provinceId, checked) => {  
        try {  
            const competitionId = state.selectedCompetition._id;  
            const province = provinces.find((prov) => prov._id === provinceId);  
            if (province) {  
                // Extract only the allowed fields  
                const updatedProvince = {  
                    province: province.province || '', // Ensure default values if undefined  
                    location: province.location || '',  
                    date: province.date ? new Date(province.date).toISOString() : null,  
                    contact_person: province.contact_person || '',  
                    contact_number: province.contact_number || '',  
                    active: checked,  
                };  
      
                await updateProvince(competitionId, provinceId, updatedProvince);  
      
                // Update the local provinces state  
                setProvinces((prevProvinces) =>  
                    prevProvinces.map((prov) =>  
                        prov._id === provinceId ? { ...prov, active: checked } : prov  
                    )  
                );  
                message.success('Province active status updated');  
            }  
        } catch (error) {  
            console.error('Error updating active status:', error);  
            message.error('Error updating active status');  
        }  
    };  

    const handleActiveProvinceSelect = async (provinceId) => {      
        try {      
          if (!user || !user._id) {      
            message.error('User not logged in');      
            return;      
          }      
          const userId = user._id;      
          await updateActiveProvince(userId, provinceId);     
          // Load existing state    
          const state = loadState();    
          // Update user in state    
          state.user.activeProvince = provinceId;    
          // Save the entire state back    
          saveState(state);    
          // Update the user state variable to trigger re-render  
          setUser(state.user);  
          message.success('Active province updated successfully!');      
        } catch (error) {      
          console.error('Error updating active province:', error);      
          message.error('Error updating active province');      
        }      
      };  

      const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleFileUpload = async (e, provinceId) => {
        e.preventDefault();
        
        try {
            await uploadCertificate(provinceId, file)
            message.success('Certificate uploaded successfully');
            setFile(null);
        } catch (error) {
            message.error(error.message);
        }
    };

    const handleFinish = async (values) => {  
        setLoading(true);  
        try {  
          await createUser({ judge: values });  
          message.success('Judge created successfully!');  
          form.resetFields();  
        } catch (error) {  
          message.error(error.message);  
        } finally {  
          setLoading(false);  
        }  
      };  

      const validatePhoneNumber = (_, value) => {
        const phoneRegex = /^[+]?[0-9]{10,15}$/;
        if (!value || phoneRegex.test(value)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('Please enter a valid phone number!'));
    };

    const validatePassword = (_, value) => {
        if (!value) {
            return Promise.reject(new Error('Please input your password'));
        }
        if (value.length < 7) {
            return Promise.reject(new Error('Password must be at least 7 characters long'));
        }
        if (value.toLowerCase().includes('password')) {
            return Promise.reject(new Error('Password cannot contain "password"'));
        }
        const specialCharacterRegex = /[!@#$%^&*(),.?":{}|<>]/;
        if (!specialCharacterRegex.test(value)) {
            return Promise.reject(new Error('Password must contain at least one special character'));
        }
        return Promise.resolve();
    };
  
    if (isLoading) {  
        return <Skeleton active />;  
    }  
  
    if (error) {  
        return <Alert message="Error" description={error} type="error" showIcon />;  
    }  
  
    return (  
        <div>  
            <Space  
                style={{ marginBottom: '15px', width: '100%' }}  
                align="center"  
                justify="space-between"  
            >  
                <Title level={4} style={{ margin: 0 }}>  
                    Settings  
                </Title>  
            </Space>  
            <Space style={{ marginBottom: '15px', width: '100%' }} direction="vertical" size={16}>  
            <Card title="Add New Judge" bordered={false}>  
              <Form  
                form={form}  
                layout="vertical"  
                onFinish={handleFinish}  
              >  
                <Form.Item  
                  label="Judge First Name"  
                  name="first_name"  
                  rules={[{ required: true, message: 'Please enter the first name' }]}  
                >  
                  <Input placeholder="Judge First Name" />  
                </Form.Item>  
        
                <Form.Item  
                  label="Judge Last Name"  
                  name="last_name"  
                  rules={[{ required: true, message: 'Please enter the last name' }]}  
                >  
                  <Input placeholder="Judge Last Name" />  
                </Form.Item>  
        
                <Form.Item  
                  label="Judge Email"  
                  name="email"  
                  rules={[  
                    { required: true, message: 'Please enter the email' },  
                    { type: 'email', message: 'Please enter a valid email' } 
                  ]}  
                >  
                  <Input placeholder="Judge Email" />  
                </Form.Item>  
        
                <Form.Item  
                  label="Judge Phone Number"  
                  name="phone"  
                  rules={[
                    { required: true, message: 'Please enter the phone number' },
                    { validator: validatePhoneNumber }
                  ]}  
                >  
                  <Input placeholder="Judge Phone Number" />  
                </Form.Item>  
        
                <Form.Item  
                  label="Judge Password"  
                  name="password"  
                  rules={[
                    { required: true, message: 'Please enter the password' },
                    { validator: validatePassword }
                ]}  
                >  
                  <Input.Password placeholder="Judge Password" />  
                </Form.Item>  
        
                <Form.Item>  
                  <Button type="primary" htmlType="submit" loading={loading}>  
                    Create Judge  
                  </Button>  
                </Form.Item>  
              </Form>  
            </Card>  
          </Space>  
            <Form layout="vertical">  
                <Space style={{ width: '100%' }} size="middle" direction="vertical">  
                <Card title="Set Active Audition Date" bordered={false}>  
                    <Form.Item label="Select Audition Date">  
                        <Select  
                        placeholder="Select an Audition Date"  
                        value={user.activeProvince ? user.activeProvince : null}  
                        onChange={handleActiveProvinceSelect}  
                        style={{ width: '100%' }}  
                        >  
                        {provinces.map((province) => (  
                            <Option key={province._id} value={province._id}>  
                            {province.province + ' - ' + province.location + ' - ' + province.date}  
                            </Option>  
                        ))}  
                        </Select>  
                    </Form.Item>  
                    </Card> 
                    <Card title="Audition Dates" bordered={false}>  
                        <Form.Item>  
                            <Button type="primary" onClick={handleAddProvince}>  
                                Add Audition  
                            </Button>  
                        </Form.Item>  
                        <List  
                            dataSource={[...provinces].sort((a, b) => new Date(a.date) - new Date(b.date))} 
                            renderItem={(province) => (  
                                <List.Item  
                                    actions={[  
                                        <Switch  
                                            checked={province.active}  
                                            onChange={(checked) =>  
                                                handleActiveStatusChange(  
                                                    province._id,  
                                                    checked  
                                                )  
                                            }  
                                            checkedChildren="Active"  
                                            unCheckedChildren="Inactive"  
                                        />,  
                                        <div>  
                                            <input  
                                                type="file"  
                                                id="certificateInput"  
                                                style={{ display: 'none' }}  
                                                onChange={handleFileChange}  
                                            />  
                                            <label htmlFor="certificateInput">  
                                                <Button icon={<UploadOutlined />} onClick={() => document.getElementById('certificateInput').click()}>  
                                                    Upload Certificate  
                                                </Button>  
                                            </label>  
                                            {file && (  
                                                <Button type="primary" onClick={(e) => handleFileUpload(e, province._id)}>  
                                                    Upload  
                                                </Button>  
                                            )}  
                                        </div> ,
                                        <Button  
                                        type="link"  
                                        onClick={() =>  
                                            handleEditProvince(province)  
                                        }  
                                    >  
                                        Edit Audition  
                                    </Button>,  
                                        <Button  
                                        type="link"  
                                        danger  
                                        onClick={() => {  
                                          Modal.confirm({  
                                            title: 'Delete Audition',  
                                            content: 'Are you sure you want to delete this audition date? This action cannot be undone.',  
                                            okText: 'Yes',  
                                            okType: 'danger',  
                                            cancelText: 'No',  
                                            onOk: () => handleDeleteProvince(province._id),  
                                          });  
                                        }}  
                                      >  
                                        Delete  
                                      </Button>  ,  
                                    ]}  
                                >  
                                    <List.Item.Meta  
                                        title={province.province}  
                                        description={`Location: ${province.location}, Date: ${  
                                            province.date  
                                                ? moment(province.date).format('YYYY-MM-DD')  
                                                : 'N/A'  
                                        }`}  
                                    />  
                                </List.Item>  
                            )}  
                        />  
                    </Card>  
                    <Card title="Banking Details" bordered={false}>  
                        <Form.Item label="Account Name">  
                            <Input  
                                type="text"  
                                name="bankingDetails.accountName"  
                                value={localSettings.bankingDetails.accountName}  
                                onChange={handleInputChange}  
                            />  
                        </Form.Item>  
                        <Form.Item label="Bank">  
                            <Input  
                                type="text"  
                                name="bankingDetails.bank"  
                                value={localSettings.bankingDetails.bank}  
                                onChange={handleInputChange}  
                            />  
                        </Form.Item>  
                        <Form.Item label="Account Type">  
                            <Input  
                                type="text"  
                                name="bankingDetails.accountType"  
                                value={localSettings.bankingDetails.accountType}  
                                onChange={handleInputChange}  
                            />  
                        </Form.Item>  
                        <Form.Item label="Account Number">  
                            <Input  
                                type="text"  
                                name="bankingDetails.accountNumber"  
                                value={localSettings.bankingDetails.accountNumber}  
                                onChange={handleInputChange}  
                            />  
                        </Form.Item>  
                        <Form.Item label="Branch Code">  
                            <Input  
                                type="text"  
                                name="bankingDetails.branchCode"  
                                value={localSettings.bankingDetails.branchCode}  
                                onChange={handleInputChange}  
                            />  
                        </Form.Item>  
                    </Card>  
                    <Card title="Contact Details" bordered={false}>  
                        <Form.Item label="Email">  
                            <Input  
                                type="email"  
                                name="contactDetails.email"  
                                value={localSettings.contactDetails.email}  
                                onChange={handleInputChange}  
                            />  
                        </Form.Item>  
                        <Form.Item label="Website">  
                            <Input  
                                type="text"  
                                name="contactDetails.website"  
                                value={localSettings.contactDetails.website}  
                                onChange={handleInputChange}  
                            />  
                        </Form.Item>  
                        <Form.Item label="Emergency Contact Name">  
                            <Input  
                                type="text"  
                                name="contactDetails.emergencyContact.name"  
                                value={localSettings.contactDetails.emergencyContact.name}  
                                onChange={handleInputChange}  
                            />  
                        </Form.Item>  
                        <Form.Item label="Emergency Contact Phone">  
                            <Input  
                                type="text"  
                                name="contactDetails.emergencyContact.phone"  
                                value={localSettings.contactDetails.emergencyContact.phone}  
                                onChange={handleInputChange}  
                            />  
                        </Form.Item>  
                    </Card>  
                    <Card title="Round Fees" bordered={false}>  
                        {localSettings.rounds.map((round, index) => (  
                            <div key={index} style={{ marginBottom: '20px' }}>  
                                <Form.Item label={`Round ${index + 1}`}>  
                                    <Form.Item label="Base Fee">  
                                        <Input  
                                            type="number"  
                                            value={round.baseFee}  
                                            onChange={(e) =>  
                                                handleRoundChange(  
                                                    index,  
                                                    'baseFee',  
                                                    e.target.value  
                                                )  
                                            }  
                                        />  
                                    </Form.Item>  
                                    <Form.Item label="Additional Fee">  
                                        <Input  
                                            type="number"  
                                            value={round.additionalFee}  
                                            onChange={(e) =>  
                                                handleRoundChange(  
                                                    index,  
                                                    'additionalFee',  
                                                    e.target.value  
                                                )  
                                            }  
                                        />  
                                    </Form.Item>  
                                </Form.Item>  
                            </div>  
                        ))}  
                    </Card>  
                    {/* <Card title="Grading Details" bordered={false}>  
                        <Form.Item label="Gold Medal Grade">  
                            <Input  
                                type="number"  
                                name="goldMedalGrade"  
                                value={localSettings.gradingDetails.goldMedalGrade}  
                                onChange={handleGradingDetailsChange}  
                            />  
                        </Form.Item>  
                        <Form.Item label="Silver Medal Grade">  
                            <Input  
                                type="number"  
                                name="silverMedalGrade"  
                                value={localSettings.gradingDetails.silverMedalGrade}  
                                onChange={handleGradingDetailsChange}  
                            />  
                        </Form.Item>  
                        <Form.Item label="Bronze Medal Grade">  
                            <Input  
                                type="number"  
                                name="bronzeMedalGrade"  
                                value={localSettings.gradingDetails.bronzeMedalGrade}  
                                onChange={handleGradingDetailsChange}  
                            />  
                        </Form.Item>  
                        <Form.Item label="Passing Grade">  
                            <Input  
                                type="number"  
                                name="passingGrade"  
                                value={localSettings.gradingDetails.passingGrade}  
                                onChange={handleGradingDetailsChange}  
                            />  
                        </Form.Item>  
                    </Card>  
                    <Card title="Image Path" bordered={false}>  
                        <Form.Item label="Image Path">  
                            <Input  
                                type="text"  
                                name="imagePath"  
                                value={localSettings.imagePath}  
                                onChange={handleInputChange}  
                            />  
                        </Form.Item>  
                        <Form.Item label="Image Path (White)">  
                            <Input  
                                type="text"  
                                name="imagePathWhite"  
                                value={localSettings.imagePathWhite}  
                                onChange={handleInputChange}  
                            />  
                        </Form.Item>  
                    </Card>   */}
                    <Card title="Selected Round" bordered={false}>  
                        <Form.Item label="Selected Round">  
                            <InputNumber  
                                min={1}  
                                max={3}  
                                value={localSettings.selectedRound}  
                                onChange={handleSelectedRoundChange}  
                            />  
                        </Form.Item>  
                    </Card>  
                    <Card title="Passport Required" bordered={false}>  
                        <Form.Item>  
                            <Checkbox  
                                name="passportRequired"  
                                checked={localSettings.passportRequired}  
                                onChange={handleCheckboxChange}  
                            >  
                                Passport Required  
                            </Checkbox>  
                        </Form.Item>  
                    </Card>  
                    <Card title="Nationals Applications Open" bordered={false}>  
                    <Form.Item>  
                      <Checkbox  
                        name="nationalApplicationsOpen"  
                        checked={localSettings.nationalApplicationsOpen}  
                        onChange={handleCheckboxChange}  
                      >  
                        National Applications Open  
                      </Checkbox>  
                    </Form.Item>  
                  </Card>  
                  <Card title="Emails Enabled" bordered={false}>  
                    <Form.Item>  
                        <Checkbox  
                        name="emailsEnabled"  
                        checked={localSettings.emailsEnabled}  
                        onChange={handleCheckboxChange}  
                        >  
                        Emails Enabled  
                        </Checkbox>  
                    </Form.Item>  
                    </Card>  
                    <Card title="Minimum User Age" bordered={false}>  
                        <Form.Item label="Minimum User Age">  
                            <InputNumber  
                                min={0}  
                                max={100}  
                                value={localSettings.minimumAge}  
                                onChange={handleMinimumAgeChange}  
                            />  
                        </Form.Item>  
                    </Card>  
                    <Card title="National Certificate" bordered={false}>  
                        <Form.Item>  
                        <div>  
                        <input  
                            type="file"  
                            id="certificateInput"  
                            style={{ display: 'none' }}  
                            onChange={handleFileChange}  
                        />  
                        <label htmlFor="certificateInput">  
                            <Button icon={<UploadOutlined />} onClick={() => document.getElementById('certificateInput').click()}>  
                                Upload Certificate  
                            </Button>  
                        </label>  
                        {file && (  
                            <Button type="primary" onClick={(e) => handleFileUpload(e, state._id)}>  
                                Upload  
                            </Button>  
                        )}  
                    </div>
                        </Form.Item>  
                    </Card>  
                    <Button type="primary" onClick={handleSaveSettings} loading={savingSettings}>  
                        Save Settings  
                    </Button>  
                </Space>  
            </Form>  
  
            <Modal  
                title="Add Audition Date"  
                visible={isAddProvinceModalVisible}  
                onOk={handleAddProvinceOk}  
                onCancel={handleAddProvinceCancel}  
            >  
                <Form layout="vertical">  
                    <Form.Item label="Province">  
                        <Input  
                            value={newProvince.province}  
                            onChange={(e) =>  
                                setNewProvince({  
                                    ...newProvince,  
                                    province: e.target.value,  
                                })  
                            }  
                        />  
                    </Form.Item>  
                    <Form.Item label="Location">  
                        <Input  
                            value={newProvince.location}  
                            onChange={(e) =>  
                                setNewProvince({  
                                    ...newProvince,  
                                    location: e.target.value,  
                                })  
                            }  
                        />  
                    </Form.Item>  
                    <Form.Item label="Date">  
                        <DatePicker  
                            value={newProvince.date}  
                            onChange={(date) => setNewProvince({ ...newProvince, date })}  
                        />  
                    </Form.Item>  
                    <Form.Item label="Contact Person">  
                        <Input  
                            value={newProvince.contact_person}  
                            onChange={(e) =>  
                                setNewProvince({  
                                    ...newProvince,  
                                    contact_person: e.target.value,  
                                })  
                            }  
                        />  
                    </Form.Item>  
                    <Form.Item label="Contact Number">  
                        <Input  
                            value={newProvince.contact_number}  
                            onChange={(e) =>  
                                setNewProvince({  
                                    ...newProvince,  
                                    contact_number: e.target.value,  
                                })  
                            }  
                        />  
                    </Form.Item>  
                    <Form.Item>  
                        <Checkbox  
                            checked={newProvince.active}  
                            onChange={(e) =>  
                                setNewProvince({  
                                    ...newProvince,  
                                    active: e.target.checked,  
                                })  
                            }  
                        >  
                            Active  
                        </Checkbox>  
                    </Form.Item>  
                </Form>  
            </Modal>  
  
            <Modal  
                title="Edit Province"  
                visible={isEditProvinceModalVisible}  
                onOk={handleEditProvinceOk}  
                onCancel={handleEditProvinceCancel}  
            >  
                {editingProvince && (  
                    <Form layout="vertical">  
                        <Form.Item label="Province">  
                            <Input  
                                value={editingProvince.province}  
                                onChange={(e) =>  
                                    setEditingProvince({  
                                        ...editingProvince,  
                                        province: e.target.value,  
                                    })  
                                }  
                            />  
                        </Form.Item>  
                        <Form.Item label="Location">  
                            <Input  
                                value={editingProvince.location}  
                                onChange={(e) =>  
                                    setEditingProvince({  
                                        ...editingProvince,  
                                        location: e.target.value,  
                                    })  
                                }  
                            />  
                        </Form.Item>  
                        <Form.Item label="Date">  
                            <DatePicker  
                                value={editingProvince.date}  
                                onChange={(date) =>  
                                    setEditingProvince({ ...editingProvince, date })  
                                }  
                            />  
                        </Form.Item>  
                        <Form.Item label="Contact Person">  
                            <Input  
                                value={editingProvince.contact_person}  
                                onChange={(e) =>  
                                    setEditingProvince({  
                                        ...editingProvince,  
                                        contact_person: e.target.value,  
                                    })  
                                }  
                            />  
                        </Form.Item>  
                        <Form.Item label="Contact Number">  
                            <Input  
                                value={editingProvince.contact_number}  
                                onChange={(e) =>  
                                    setEditingProvince({  
                                        ...editingProvince,  
                                        contact_number: e.target.value,  
                                    })  
                                }  
                            />  
                        </Form.Item>  
                        <Form.Item>  
                            <Checkbox  
                                checked={editingProvince.active}  
                                onChange={(e) =>  
                                    setEditingProvince({  
                                        ...editingProvince,  
                                        active: e.target.checked,  
                                    })  
                                }  
                            >  
                                Active  
                            </Checkbox>  
                        </Form.Item>  
                    </Form>  
                )}  
            </Modal>  
        </div>  
    );  
};  
  
export default Settings;  